import { toast } from 'react-hot-toast';

const MAX_FILE_SIZE = 500 * 1024; // 500KB in bytes
const MAX_WIDTH = 1920;
const QUALITY = 0.8;

// Special handling for street view images - larger size limit and PNG format
const STREET_VIEW_MAX_SIZE = 2 * 1024 * 1024; // 2MB for street view images
const STREET_VIEW_QUALITY = 0.9; // Higher quality for street view

export const compressImage = async (file: File | Blob, isStreetView: boolean = false): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        // Set white background for JPEG conversion (only for non-street view images)
        if (!isStreetView) {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, width, height);
        }
        
        // Improve rendering quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        // Use different formats and quality based on image type
        const format = isStreetView ? 'image/png' : 'image/jpeg';
        const quality = isStreetView ? STREET_VIEW_QUALITY : QUALITY;
        const maxSize = isStreetView ? STREET_VIEW_MAX_SIZE : MAX_FILE_SIZE;
        
        // Convert to blob with appropriate format and quality
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            
            // If the compressed size is still too large, reduce quality more gradually
            if (blob.size > maxSize && !isStreetView) {
              const ratio = maxSize / blob.size;
              const newQuality = Math.max(quality * ratio, 0.65);
              
              canvas.toBlob(
                (finalBlob) => {
                  if (!finalBlob) {
                    reject(new Error('Failed to compress image'));
                    return;
                  }
                  
                  const fileName = file instanceof File ? file.name : `compressed-image.${isStreetView ? 'png' : 'jpg'}`;
                  const compressedFile = new File([finalBlob], fileName, {
                    type: format,
                    lastModified: Date.now(),
                  });
                  
                  resolve(compressedFile);
                },
                format,
                newQuality
              );
            } else {
              const fileName = file instanceof File ? file.name : `compressed-image.${isStreetView ? 'png' : 'jpg'}`;
              const compressedFile = new File([blob], fileName, {
                type: format,
                lastModified: Date.now(),
              });
              
              resolve(compressedFile);
            }
          },
          format,
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });
};

export const processImages = async (files: Array<File | Blob>, isStreetView: boolean = false): Promise<File[]> => {
  try {
    const compressedFiles = await Promise.all(
      files.map(async (file) => {
        const maxSize = isStreetView ? STREET_VIEW_MAX_SIZE : MAX_FILE_SIZE;
        
        // Skip compression if file is already small enough
        if (file.size <= maxSize) {
          const extension = isStreetView ? 'png' : 'jpg';
          return file instanceof File ? file : new File([file], `image.${extension}`, { 
            type: isStreetView ? 'image/png' : 'image/jpeg' 
          });
        }
        return compressImage(file, isStreetView);
      })
    );
    
    return compressedFiles;
  } catch (error) {
    console.error('Error processing images:', error);
    toast.error('שגיאה בעיבוד התמונות');
    return [];
  }
};

export const compressBlob = async (blob: Blob, isStreetView: boolean = false): Promise<File> => {
  try {
    // If it's a street view image, don't compress at all - return as is
    if (isStreetView) {
      return new File([blob], 'street-view.png', { 
        type: 'image/png',
        lastModified: Date.now()
      });
    }
    
    const [compressedFile] = await processImages([blob], isStreetView);
    return compressedFile;
  } catch (error) {
    console.error('Error compressing blob:', error);
    throw error;
  }
};